import axios from "axios";
import { type PropsWithChildren, useContext, useEffect } from "react";

import { SecurityProvider } from "iqf-web-ui/security";
import { MeProvider } from "iqf-web-ui/security/me";
import { WebSocketContext } from "iqf-web-ui/web-socket";

import { type UdzsAuthClient } from "./auth-client-types";

export type UdzsAuthProviderProps = {
  authClient: UdzsAuthClient;
  meApi: string;
  onForbidden?: () => void;
};

export function UdzsAuthProvider({
  children,
  authClient,
  meApi,
  onForbidden,
}: PropsWithChildren<UdzsAuthProviderProps>) {
  if (typeof window === "undefined") {
    return children;
  }

  return (
    <MeProvider
      api={meApi}
      enabled={false}
      onError={async (error) => {
        if (error.response?.status === 401) {
          authClient.clearLocalAuth();
          return null;
        }

        if (error.response?.status === 403) {
          onForbidden?.();
          return null;
        }

        throw error;
      }}
    >
      <UdzsAuthBridge authClient={authClient}>{children}</UdzsAuthBridge>
    </MeProvider>
  );
}

function UdzsAuthBridge({
  children,
  authClient,
}: PropsWithChildren<{ authClient: UdzsAuthClient }>) {
  const { setConnectHeaders } = useContext(WebSocketContext);

  useEffect(() => {
    authClient.applyAxiosAuthorization();
    authClient.startRefreshScheduler();

    const unsubscribe = authClient.subscribeToken((token) => {
      const authorization = token ? `Bearer ${token}` : "";

      axios.defaults.headers.common.Authorization = authorization;

      if (authorization) {
        setConnectHeaders({ Authorization: authorization });
      }
    });

    return () => {
      unsubscribe();
      authClient.stopRefreshScheduler();
    };
  }, [authClient, setConnectHeaders]);

  return (
    <SecurityProvider
      value={{
        isLoggedIn: authClient.hasStoredAuth(),
        login: authClient.login,
        logout: authClient.logout,
      }}
    >
      {children}
    </SecurityProvider>
  );
}
